import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { initMetaPixel, trackPixelEvent } from "@/lib/metaPixel";

/**
 * Mounts the Meta Pixel for the whole app (every route, including /admin and
 * the student dashboard) and fires a PageView on every navigation.
 *
 * A plain copy-pasted base code only fires PageView once, on the document's
 * first load. This is a client-routed SPA: after that first load, moving
 * between pages never reloads the document, so a plain install would only
 * ever see one PageView no matter how many pages a visitor viewed. Re-firing
 * on every pathname/search change (mirroring src/components/ScrollToTop.tsx,
 * which solves the same "SPA navigation isn't a real navigation" problem) is
 * what makes per-page tracking actually work.
 *
 * Rendered once near the app root (see src/App.tsx), inside <BrowserRouter>
 * so useLocation is available.
 */
const MetaPixel = () => {
  const { pathname, search } = useLocation();
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      initMetaPixel();
    }
    trackPixelEvent("PageView");
  }, [pathname, search]);

  return null;
};

export default MetaPixel;
